/**
 * Which "apply to all" checkboxes a TEMPLATE anchor is allowed to show.
 *
 * Change All on a template reuses the whole /pro drawer, so every checkbox already
 * wired for orders would otherwise appear — including ones beside fields the template
 * policy renders read-only. A checkbox on a locked field is worse than useless: it
 * offers to fan out a value the anchor cannot even change, and the save then refuses it
 * per record.
 *
 * So the rule Lee set is: on a template, a checkbox appears only on a field that is
 * CURRENTLY unlocked (State 3, or State 2 while the template is unapproved) and is not
 * a leg field. This module is that rule.
 *
 * THE KEY DETAIL — two different key spaces.
 * `templateFieldPolicy` is keyed by the field the USER sees (`whseCsr`, `billToName`,
 * `consigneeName`); a change-all checkbox is keyed by the field's VALUE key, the one
 * `setField` and the save payload use (`whseCsrUsername`, `billTo`, `consigneeCode`) —
 * for a lookup-backed field only the FK is writable, so the two names differ. Mapping
 * them is the whole job of POLICY_KEY_FOR below, and getting it wrong fails silently in
 * the direction that matters least visibly: the checkbox just never appears.
 *
 * The server enforces the same policy independently (template_change_all_fields.py) —
 * this decides what to DRAW, never what is permitted.
 */
import { templateFieldEditable } from '../templateFieldPolicy';

/**
 * Change-all key -> policy key, for the fields whose two names differ. Everything else
 * is looked up under its own name.
 *
 * Read it as "the checkbox called X asks the policy about the field called Y".
 */
export const POLICY_KEY_FOR: Readonly<Record<string, string>> = {
  whseCsrUsername: 'whseCsr',
  carrierCode: 'carrier',
  consigneeCode: 'consigneeName',
  brokerCode: 'brokerName',
  brokerContactRecno: 'brokerContactName',
  customer: 'customerName',
  portOfOriginCode: 'portOfOrigin',
  portOfDischargeCode: 'portOfDischarge',
  billTo: 'billToName',
  billToContactRecno: 'billToContactName',
  // The Invoicing tab's grouped checkboxes: one tick fans a PAIR of columns, so the
  // group asks about the field the pair belongs to. `quoteLink` is quotenumber +
  // revisionnumber, both State 2.
  quoteLink: 'quoteNumber',
};

/**
 * Checkboxes never shown on a template, even though the policy would allow the field.
 *
 * `addlInfoDeleted` is State 3, so without naming it here bulk template DELETE would be
 * available by default — and delete on an order is deliberately armed by its own
 * checkbox plus a reason prompt, machinery templates do not have. `invoiceApproval` IS
 * the approval switch: fanning it would lock every State-2 field on the targets and
 * fire the order_approved rule's mail across the set. Neither was asked for.
 *
 * Kept as an explicit list rather than left to the policy so the reason is written down
 * where the decision is made. The server refuses both by name too.
 */
export const TEMPLATE_BULK_EXCLUDED: ReadonlySet<string> = new Set([
  'addlInfoDeleted',
  'invoiceApproval',
]);

/**
 * May this change-all checkbox be shown on a template in this approval state?
 *
 * Only call this for templates — an order keeps its own gate (the display rules).
 */
export function templateBulkFieldAllowed(
  bulkFieldName: string,
  opts: { isApproved: boolean },
): boolean {
  if (TEMPLATE_BULK_EXCLUDED.has(bulkFieldName)) return false;
  const policyKey = POLICY_KEY_FOR[bulkFieldName] ?? bulkFieldName;
  return templateFieldEditable(policyKey, opts);
}
